import { computed, getCurrentInstance, ref, shallowRef, unref } from 'vue'
import { isArray, isNumber, rAF } from '@jdesign-vue/utils'
import { FixedDir } from '../constants'

import type { Ref } from 'vue'
import type { TableV2Props } from '../table'
import type {
  RowExpandParams,
  RowHeightChangedParams,
  RowHoverParams,
} from '../row'
import type { FixedDirection, KeyType } from '../types'
import type { onRowRenderedParams } from '../grid'
import type { TableGridInstance } from '../table-grid'

type Heights = Record<KeyType, number>
type GridInstanceRef = Ref<TableGridInstance | undefined>

type UseRowProps = {
  mainTableRef: GridInstanceRef
  leftTableRef: GridInstanceRef
  rightTableRef: GridInstanceRef
  isScrolling: Ref<boolean>
}

export const useRow = (
  props: TableV2Props,
  { mainTableRef, leftTableRef, rightTableRef, isScrolling }: UseRowProps
) => {
  const vm = getCurrentInstance()!
  const { emit } = vm
  const isResetting = shallowRef(false)
  const hoveringRowKey = shallowRef<KeyType | null>(null)
  const expandedRowKeys = ref<KeyType[]>(props.defaultExpandedRowKeys || [])
  const lastRenderedRowIndex = ref(-1)
  const resetIndex = shallowRef<number | null>(null)
  const rowHeights = ref<Heights>({})
  const pendingRowHeights = ref<Heights>({})
  const leftTableHeights = shallowRef<Heights>({})
  const mainTableHeights = shallowRef<Heights>({})
  const rightTableHeights = shallowRef<Heights>({})
  const isDynamic = computed(() => isNumber(props.estimatedRowHeight))

  function onRowsRendered(params: onRowRenderedParams) {
    props.onRowsRendered?.(params)

    if (params.rowCacheEnd > unref(lastRenderedRowIndex)) {
      lastRenderedRowIndex.value = params.rowCacheEnd
    }
  }

  function onRowHovered({ hovered, rowKey }: RowHoverParams) {
    // 滚动中鼠标会快速划过大量行，同步 hoveringRowKey 会触发三表 + 所有可见行 patch
    //（Performance 里 mouseenter/mouseleave → patchKeyedChildren 的主因）
    if (unref(isScrolling)) return
    hoveringRowKey.value = hovered ? rowKey : null
  }

  function onRowExpanded({
    expanded,
    rowData,
    rowIndex,
    rowKey,
  }: RowExpandParams) {
    const _expandedRowKeys = [...unref(expandedRowKeys)]
    const currentKeyIndex = _expandedRowKeys.indexOf(rowKey)
    if (expanded) {
      if (currentKeyIndex === -1) _expandedRowKeys.push(rowKey)
    } else {
      if (currentKeyIndex > -1) _expandedRowKeys.splice(currentKeyIndex, 1)
    }
    expandedRowKeys.value = _expandedRowKeys

    emit('update:expandedRowKeys', _expandedRowKeys)
    props.onRowExpand?.({
      expanded,
      rowData,
      rowIndex,
      rowKey,
    })
    // If this is not controlled, then use this to notify changes
    props.onExpandedRowsChange?.(_expandedRowKeys)
  }

  /**
   * 展开所有树形节点
   * @param data - 表格数据
   */
  function expandAllRows(data: any[]) {
    const { rowKey: rowKeyProp } = props
    const allKeys: KeyType[] = []
    
    function collectKeys(items: any[]) {
      items.forEach((item) => {
        if (isArray(item.children) && item.children.length > 0) {
          allKeys.push(item[rowKeyProp])
          collectKeys(item.children)
        }
      })
    }
    
    collectKeys(data)
    expandedRowKeys.value = allKeys
    emit('update:expandedRowKeys', allKeys)
    props.onExpandedRowsChange?.(allKeys)
  }

  /**
   * 收起所有树形节点
   */
  function collapseAllRows() {
    expandedRowKeys.value = []
    emit('update:expandedRowKeys', [])
    props.onExpandedRowsChange?.([])
  }

  // 用 rAF 合并同一帧内多个行高变更，避免每次变更都触发一轮 forceUpdate
  let flushHandle: number | null = null
  const flushingRowHeights = () => {
    if (flushHandle != null) return
    flushHandle = rAF(() => {
      flushHandle = null
      isResetting.value = true
      rowHeights.value = { ...unref(rowHeights), ...unref(pendingRowHeights) }
      // resetAfterIndex(idx, true) 内部已经处理 lastVisitedRowIndex + 清 style 缓存，
      // 并对三个 Grid 各触发一次 $forceUpdate，等价于旧实现里那串显式 forceUpdate，
      // 同时省掉父组件的 $forceUpdate（rowHeights/isResetting 变化已能触发重渲染）
      resetAfterIndex(unref(resetIndex)!, true)
      pendingRowHeights.value = {}
      resetIndex.value = null
      isResetting.value = false
    })
  }

  function resetAfterIndex(index: number, forceUpdate = false) {
    if (!unref(isDynamic)) return
    ;[mainTableRef, leftTableRef, rightTableRef].forEach((tableRef) => {
      const table = unref(tableRef)
      if (table) table.resetAfterRowIndex(index, forceUpdate)
    })
  }

  // 完全重置所有缓存（用于展开/折叠时）
  function resetAllCache() {
    if (!unref(isDynamic)) return
    ;[mainTableRef, leftTableRef, rightTableRef].forEach((tableRef) => {
      const table = unref(tableRef)
      if (table?.resetAllCache) {
        table.resetAllCache()
      }
    })
  }

  function resetHeights(rowKey: KeyType, height: number, rowIdx: number) {
    const resetIdx = unref(resetIndex)
    if (resetIdx === null) {
      resetIndex.value = rowIdx
    } else {
      if (resetIdx > rowIdx) {
        resetIndex.value = rowIdx
      }
    }

    pendingRowHeights.value[rowKey] = height
  }

  function onRowHeightChange(
    { rowKey, height, rowIndex }: RowHeightChangedParams,
    fixedDir: FixedDirection
  ) {
    if (!fixedDir) {
      mainTableHeights.value[rowKey] = height
    } else {
      if (fixedDir === FixedDir.RIGHT) {
        rightTableHeights.value[rowKey] = height
      } else {
        leftTableHeights.value[rowKey] = height
      }
    }

    const maximumHeight = Math.max(
      ...[leftTableHeights, rightTableHeights, mainTableHeights].map(
        (records) => records.value[rowKey] || 0
      )
    )

    if (unref(rowHeights)[rowKey] !== maximumHeight) {
      resetHeights(rowKey, maximumHeight, rowIndex)
      flushingRowHeights()
    }
  }

  return {
    hoveringRowKey,
    expandedRowKeys,
    lastRenderedRowIndex,
    isDynamic,
    isResetting,
    rowHeights,

    resetAfterIndex,
    resetAllCache,
    onRowExpanded,
    onRowHovered,
    onRowsRendered,
    onRowHeightChange,
    expandAllRows,
    collapseAllRows,
  }
}

export type UseRowReturn = ReturnType<typeof useRow>
